import {OtsDateTime} from "../components/OtsDateTime";
import {useAppDensity} from "../app/AppContext";
import {OtsBadge} from "../components/OtsBadge";
import {OtsSelectableList, type OtsSelectableListItem} from "../components/OtsSelectableList";
import {StatusPill} from "../components/StatusPill";
import {compareGaugeQuantity, formatGaugeQuantity, gaugeQuantityStatus} from "./gauge-quantity-display";
import type {GaugeQuantityAudit} from "./gauge-quantity-types";

/** Selection id for an unsaved "Calculate now" result listed above the history. */
export const GAUGE_QUANTITY_PREVIEW_ID = "preview";

export type GaugeQuantityAuditListProps = {
  audits: GaugeQuantityAudit[];
  selectedId?: string;
  onSelect: (id: string) => void;
  /** Lists an unsaved current calculation first, selectable as {@link GAUGE_QUANTITY_PREVIEW_ID}. */
  previewAvailable?: boolean;
  /** Tighter rows for embedded hosts. Defaults to the app density. */
  compact?: boolean;
};

/** Newest-first calculation history. Each row says what was calculated and whether it matches the ticket. */
export const GaugeQuantityAuditList = ({audits, selectedId, onSelect, previewAvailable = false, compact: compactProp}: GaugeQuantityAuditListProps) => {
  const density = useAppDensity();
  const compact = compactProp ?? density === "compact";
  const text = compact ? "text-xs" : "text-sm";
  const items: OtsSelectableListItem[] = audits.map(audit => {
    const status = gaugeQuantityStatus(audit.result.status);
    const comparison = compareGaugeQuantity(audit);
    return {id: audit.id, content: <>
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs text-[var(--ots-text-muted)]"><OtsDateTime value={audit.recordedDttm} /></span>
        <span className="shrink-0 whitespace-nowrap"><StatusPill size="compact" tone={status.tone}>{status.label}</StatusPill></span>
      </div>
      <p className={`font-semibold text-[var(--ots-text)] ${compact ? "mt-0.5" : "mt-1"} ${text}`}>
        {formatGaugeQuantity(audit.result.quantity, audit.result.quantityUnit)}
      </p>
      <OtsBadge className={compact ? "mt-0.5" : "mt-1"} size="compact" tone={comparison.tone}>{comparison.label}</OtsBadge>
    </>};
  });
  if (previewAvailable)
    items.unshift({id: GAUGE_QUANTITY_PREVIEW_ID, content: <>
      <p className={`font-semibold text-[var(--ots-text)] ${text}`}>Current calculation</p>
      <p className="text-xs text-[var(--ots-text-muted)]">Not saved</p>
    </>});
  return <OtsSelectableList ariaLabel="Calculation history" compact={compact} items={items} selectedId={selectedId} onSelect={onSelect}/>;
};
